import React, { useState, useEffect, useMemo } from 'react';
import { Circle, CircleDot, CheckCircle2, Plus, AlertTriangle, Package, Activity, DollarSign, CheckCircle, TrendingUp, ExternalLink, Calendar, Wrench, TrendingDown, Eye, Truck, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import VehicleTireMap from '../components/VehicleTireMap';
import TiresTable from '../components/TiresTable';
import Pagination from '../components/Pagination';
import BulkTireModal from '../components/BulkTireModal';
import CsvImportModal from '../components/CsvImportModal';
import TemplateManager from '../components/TemplateManager';
import TireSelectionModal from '../components/TireSelectionModal';

// Hook de debounce para otimizar performance dos filtros
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const TiresPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [tires, setTires] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lastInspection, setLastInspection] = useState(null);
  const [vehiclesWithTires, setVehiclesWithTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [selectedTire, setSelectedTire] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTireDetails, setSelectedTireDetails] = useState(null);
  const [tireHistory, setTireHistory] = useState([]);
  const [detailsTab, setDetailsTab] = useState('info');

  // Estados para paginação e tabela
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [pagination, setPagination] = useState(null);

  // Estados para modais de cadastro em lote
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Estados para instalação interativa
  const [showTireSelectionModal, setShowTireSelectionModal] = useState(false);
  const [installPosition, setInstallPosition] = useState(null);
  const [installPositionLabel, setInstallPositionLabel] = useState(null);
  const [installVehicle, setInstallVehicle] = useState(null);

  // Filtros para Instalações
  const [installFilters, setInstallFilters] = useState({
    startDate: '',
    endDate: '',
    licensePlate: '',
    vehicleId: '',
    tireId: ''
  });

  // Filtros para Alertas
  const [alertFilters, setAlertFilters] = useState({
    startDate: '',
    endDate: '',
    licensePlate: '',
    vehicleId: '',
    tireId: ''
  });

  // Aplicar debounce aos filtros de texto para otimizar performance
  const debouncedInstallLicensePlate = useDebounce(installFilters.licensePlate);
  const debouncedAlertLicensePlate = useDebounce(alertFilters.licensePlate);

  const [formData, setFormData] = useState({
    serial_number: '',
    brand: '',
    model: '',
    size: '',
    tire_type: 'new',
    category: 'drive',
    purchase_date: '',
    purchase_price: '',
    expected_lifespan_km: '',
    current_tread_depth_mm: '',
    cost_per_mm: '',
    notes: ''
  });

  const [installFormData, setInstallFormData] = useState({
    vehicle_id: '',
    position: '',
    installation_date: new Date().toISOString().split('T')[0],
    installation_odometer: '',
    installation_tread_depth_mm: '',
    installer_name: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filterStatus, page, limit, sortBy, sortOrder, searchTerm, brandFilter]);

  useEffect(() => {
    if (showInstallModal) {
      loadVehicles();
    }
  }, [showInstallModal]);

  // Auto-calcular custo por mm
  useEffect(() => {
    if (formData.purchase_price && formData.current_tread_depth_mm) {
      const price = parseFloat(formData.purchase_price);
      const depth = parseFloat(formData.current_tread_depth_mm);
      if (!isNaN(price) && !isNaN(depth) && depth > 0) {
        const costPerMm = price / depth;
        setFormData(prev => ({ ...prev, cost_per_mm: costPerMm.toFixed(2) }));
      }
    }
  }, [formData.purchase_price, formData.current_tread_depth_mm]);

  // Auto-calcular vida útil esperada em KM
  useEffect(() => {
    if (formData.tire_type && formData.category && formData.current_tread_depth_mm) {
      const depth = parseFloat(formData.current_tread_depth_mm);
      if (!isNaN(depth) && depth > 0) {
        // Base de cálculo: ~600km por mm de profundidade
        let baseKm = depth * 600;

        // Fator por tipo de pneu
        let typeFactor = 1.0;
        if (formData.tire_type === 'new') typeFactor = 1.0;
        else if (formData.tire_type === 'retreaded') typeFactor = 0.7;
        else if (formData.tire_type === 'used') typeFactor = 0.4;

        // Fator por categoria/posição
        let categoryFactor = 1.0;
        if (formData.category === 'steer') categoryFactor = 0.9;  // Dianteiro desgasta mais
        else if (formData.category === 'drive') categoryFactor = 1.0;  // Tração
        else if (formData.category === 'trailer') categoryFactor = 1.1;  // Reboque dura mais
        else if (formData.category === 'spare') categoryFactor = 1.5;  // Estepe dura muito mais

        const expectedLifespan = Math.round(baseKm * typeFactor * categoryFactor);
        setFormData(prev => ({ ...prev, expected_lifespan_km: expectedLifespan.toString() }));
      }
    }
  }, [formData.tire_type, formData.category, formData.current_tread_depth_mm]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'dashboard') {
        const dashRes = await api.get('/api/tires/dashboard');
        setDashboard(dashRes.data.data);

        // Buscar última inspeção do sistema unificado
        try {
          const lastInspRes = await api.get('/api/inspections', {
            params: {
              category: 'vehicle',
              limit: 1,
              sort: 'inspection_date:desc'
            }
          });
          if (lastInspRes.data.data && lastInspRes.data.data.length > 0) {
            setLastInspection(lastInspRes.data.data[0]);
          }
        } catch (err) {
          console.log('Não foi possível carregar última inspeção');
        }
      } else if (activeTab === 'by-vehicle') {
        const vehiclesRes = await api.get('/api/tires/by-vehicle');
        setVehiclesWithTires(vehiclesRes.data.data || []);
      } else if (activeTab === 'tires') {
        const params = {
          page,
          limit,
          sortBy,
          sortOrder
        };

        if (filterStatus && filterStatus !== 'all') {
          params.status = filterStatus;
        }

        if (searchTerm) {
          params.search = searchTerm;
        }

        if (brandFilter) {
          params.brand = brandFilter;
        }

        const tiresRes = await api.get('/api/tires', { params });
        setTires(tiresRes.data.data || []);
        setPagination(tiresRes.data.pagination || null);
      } else if (activeTab === 'installations') {
        const instRes = await api.get('/api/tires/installations');
        setInstallations(instRes.data.data || []);
      } else if (activeTab === 'alerts') {
        const alertsRes = await api.get('/api/tires/alerts', { params: { status: 'pending' } });
        setAlerts(alertsRes.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Handlers para tabela paginada
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order se já estiver ordenando por essa coluna
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(1); // Resetar para primeira página
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post('/api/tires', {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        expected_lifespan_km: formData.expected_lifespan_km ? parseInt(formData.expected_lifespan_km) : null,
        current_tread_depth_mm: formData.current_tread_depth_mm ? parseFloat(formData.current_tread_depth_mm) : null
      });

      toast.success('Pneu cadastrado com sucesso!');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      toast.error(error.response?.data?.message || 'Erro ao cadastrar pneu');
    }
  };

  const resetForm = () => {
    setFormData({
      serial_number: '',
      brand: '',
      model: '',
      size: '',
      tire_type: 'new',
      category: 'drive',
      purchase_date: '',
      purchase_price: '',
      expected_lifespan_km: '',
      current_tread_depth_mm: '',
      notes: ''
    });
  };

  const resetInstallForm = () => {
    setInstallFormData({
      vehicle_id: '',
      position: '',
      installation_date: new Date().toISOString().split('T')[0],
      installation_odometer: '',
      installation_tread_depth_mm: '',
      installer_name: ''
    });
  };

  const loadVehicles = async () => {
    try {
      const response = await api.get('/api/vehicles');
      console.log('Resposta da API de veículos:', response.data);

      // A API pode retornar { success: true, data: [...] } ou diretamente [...]
      const vehiclesData = response.data?.data || response.data || [];
      console.log('Veículos processados:', vehiclesData);

      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      toast.error('Erro ao carregar veículos');
      setVehicles([]);
    }
  };

  const handleInstallTire = async (e) => {
    e.preventDefault();

    if (!selectedTire) return;

    try {
      await api.post('/api/tires/install', {
        tire_id: selectedTire.id,
        ...installFormData,
        installation_odometer: parseInt(installFormData.installation_odometer),
        installation_tread_depth_mm: installFormData.installation_tread_depth_mm
          ? parseFloat(installFormData.installation_tread_depth_mm)
          : null
      });

      toast.success('Pneu instalado com sucesso!');
      setShowInstallModal(false);
      setSelectedTire(null);
      resetInstallForm();
      loadData();
    } catch (error) {
      console.error('Erro ao instalar pneu:', error);
      toast.error(error.response?.data?.message || 'Erro ao instalar pneu');
    }
  };

  const openInstallModal = (tire) => {
    setSelectedTire(tire);
    setShowInstallModal(true);
  };

  const handleViewDetails = async (tire) => {
    setSelectedTireDetails(tire);
    setDetailsTab('info');
    setShowDetailsModal(true);
    await loadTireHistory(tire.id);
  };

  const loadTireHistory = async (tireId) => {
    try {
      // Carregar histórico de instalações
      const installRes = await api.get('/api/tires/installations', {
        params: { tire_id: tireId }
      });

      // Carregar histórico de inspeções
      const inspectRes = await api.get('/api/tires/inspections', {
        params: { tire_id: tireId }
      });

      // Carregar histórico de alertas
      const alertsRes = await api.get('/api/tires/alerts', {
        params: { tire_id: tireId }
      });

      // Combinar e ordenar tudo por data
      const history = [
        ...installRes.data.data.map(item => ({
          ...item,
          type: 'installation',
          date: item.installation_date || item.removal_date
        })),
        ...inspectRes.data.data.map(item => ({
          ...item,
          type: 'inspection',
          date: item.inspection_date
        })),
        ...alertsRes.data.data.map(item => ({
          ...item,
          type: 'alert',
          date: item.created_at
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));

      setTireHistory(history);
    } catch (error) {
      console.error('Erro ao carregar histórico do pneu:', error);
      toast.error('Erro ao carregar histórico do pneu');
    }
  };

  const getStatusColor = (status) => {
    if (status === 'installed') return 'text-green-600 bg-green-100';
    if (status === 'in_stock') return 'text-blue-600 bg-blue-100';
    if (status === 'damaged') return 'text-red-600 bg-red-100';
    if (status === 'storage') return 'text-gray-600 bg-gray-100';
    return 'text-orange-600 bg-orange-100';
  };

  const getStatusLabel = (status) => {
    const labels = {
      installed: 'Instalado',
      in_stock: 'Em Estoque',
      storage: 'Armazenado',
      damaged: 'Danificado',
      scrapped: 'Sucateado'
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      steer: 'Dianteiro',
      drive: 'Tração',
      trailer: 'Reboque',
      spare: 'Estepe'
    };
    return labels[category] || category;
  };

  const getStatusIcon = (status) => {
    if (status === 'installed') {
      return <CheckCircle2 className="w-6 h-6 text-green-600 fill-green-100" />;
    }
    if (status === 'in_stock') {
      return <CircleDot className="w-6 h-6 text-blue-600 fill-blue-100" />;
    }
    if (status === 'storage') {
      return <CircleDot className="w-6 h-6 text-blue-500 fill-blue-50" />;
    }
    if (status === 'damaged') {
      return <CircleDot className="w-6 h-6 text-red-600 fill-red-100" />;
    }
    if (status === 'scrapped') {
      return <Circle className="w-6 h-6 text-gray-400" />;
    }
    return <Circle className="w-6 h-6 text-gray-400" />;
  };

  const getSeverityColor = (severity) => {
    if (severity === 'critical') return 'text-red-600 bg-red-100';
    if (severity === 'high') return 'text-orange-600 bg-orange-100';
    if (severity === 'medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-blue-600 bg-blue-100';
  };

  // Cores para os gráficos
  const CHART_COLORS = {
    installed: '#22c55e',
    in_stock: '#3b82f6',
    storage: '#6b7280',
    damaged: '#ef4444',
    scrapped: '#9ca3af'
  };

  // Filtrar instalações
  const filteredInstallations = useMemo(() => {
    return installations
      .map(inst => {
        // Enrich installation with vehicle and tire data
        const vehicle = vehicles.find(v => v.id === inst.vehicle_id || v.license_plate === inst.license_plate);
        const tire = tires.find(t => t.id === inst.tire_id);
        return {
          ...inst,
          vehicle,
          tire,
          vehicle_brand: vehicle?.brand,
          vehicle_model: vehicle?.model,
          vehicle_name: vehicle?.name
        };
      })
      .filter(inst => {
        // Filtro por data de instalação
        if (installFilters.startDate) {
          const instDate = new Date(inst.installation_date);
          const startDate = new Date(installFilters.startDate);
          if (instDate < startDate) return false;
        }

        if (installFilters.endDate) {
          const instDate = new Date(inst.installation_date);
          const endDate = new Date(installFilters.endDate);
          if (instDate > endDate) return false;
        }

        // Filtro por placa do veículo (com debounce)
        if (debouncedInstallLicensePlate) {
          const searchLower = debouncedInstallLicensePlate.toLowerCase();
          if (!inst.license_plate?.toLowerCase().includes(searchLower)) {
            return false;
          }
        }

        // Filtro por ID do veículo
        if (installFilters.vehicleId) {
          if (inst.vehicle_id?.toString() !== installFilters.vehicleId.toString()) {
            return false;
          }
        }

        // Filtro por ID do pneu
        if (installFilters.tireId) {
          if (inst.tire_id?.toString() !== installFilters.tireId.toString()) {
            return false;
          }
        }

        return true;
      });
  }, [installations, installFilters.startDate, installFilters.endDate, installFilters.vehicleId, installFilters.tireId, debouncedInstallLicensePlate, vehicles, tires]);

  // Filtrar alertas
  const filteredAlerts = useMemo(() => {
    return alerts
      .map(alert => {
        // Enrich alert with vehicle and tire data
        const vehicle = vehicles.find(v => v.license_plate === alert.license_plate || v.id === alert.vehicle_id);
        const tire = tires.find(t => t.id === alert.tire_id);
        return {
          ...alert,
          vehicle,
          tire
        };
      })
      .filter(alert => {
        // Filter by date range (detected_at)
        if (alertFilters.startDate) {
          const alertDate = new Date(alert.detected_at || alert.detected_date);
          const startDate = new Date(alertFilters.startDate);
          if (alertDate < startDate) return false;
        }

        if (alertFilters.endDate) {
          const alertDate = new Date(alert.detected_at || alert.detected_date);
          const endDate = new Date(alertFilters.endDate);
          if (alertDate > endDate) return false;
        }

        // Filter by license plate (com debounce)
        if (debouncedAlertLicensePlate) {
          const searchLower = debouncedAlertLicensePlate.toLowerCase();
          const licensePlate = alert.vehicle?.license_plate || alert.license_plate || '';
          if (!licensePlate.toLowerCase().includes(searchLower)) {
            return false;
          }
        }

        // Filter by vehicle (name, brand, or model)
        if (alertFilters.vehicle) {
          const searchLower = alertFilters.vehicle.toLowerCase();
          const vehicleName = alert.vehicle?.name?.toLowerCase() || '';
          const vehicleBrand = alert.vehicle?.brand?.toLowerCase() || '';
          const vehicleModel = alert.vehicle?.model?.toLowerCase() || '';
          if (!vehicleName.includes(searchLower) &&
              !vehicleBrand.includes(searchLower) &&
              !vehicleModel.includes(searchLower)) {
            return false;
          }
        }

        // Filter by tire (serial number)
        if (alertFilters.tire) {
          const searchLower = alertFilters.tire.toLowerCase();
          const tireSerial = alert.tire?.serial_number?.toLowerCase() || '';
          if (!tireSerial.includes(searchLower)) {
            return false;
          }
        }

        return true;
      });
  }, [alerts, alertFilters.startDate, alertFilters.endDate, alertFilters.vehicle, alertFilters.tire, debouncedAlertLicensePlate, vehicles, tires]);

  const STATUS_LABELS = {
    installed: 'Instalados',
    in_stock: 'Em Estoque',
    storage: 'Armazenados',
    damaged: 'Danificados',
    scrapped: 'Sucateados'
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestão de Pneus
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Controle de inventário, instalações e inspeções
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Novo Pneu
          </button>

          <button
            onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Package className="w-5 h-5" />
            Cadastro em Lote
          </button>

          <button
            onClick={() => setShowCsvModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Circle className="w-5 h-5" />
            Importar CSV
          </button>

          <button
            onClick={() => setShowTemplateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Activity className="w-5 h-5" />
            Templates
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('by-vehicle')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'by-vehicle'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Truck className="w-4 h-4 inline mr-2" />
              Por Veículo
            </button>
            <button
              onClick={() => setActiveTab('tires')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'tires'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Circle className="w-4 h-4 inline mr-2" />
              Inventário
            </button>
            <button
              onClick={() => setActiveTab('installations')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'installations'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Instalações
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'alerts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Alertas
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && dashboard && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium">Instalados</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">
                        {dashboard.summary.installed}
                      </p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Em Estoque</p>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                        {dashboard.summary.in_stock}
                      </p>
                    </div>
                    <Package className="w-6 h-6 text-blue-500" />
                  </div>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Alertas</p>
                      <p className="text-2xl font-bold text-orange-700 dark:text-orange-300 mt-1">
                        {dashboard.summary.pending_alerts}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Inspeções</p>
                      <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                        {dashboard.summary.upcoming_inspections}
                      </p>
                    </div>
                    <Activity className="w-6 h-6 text-purple-500" />
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Inventário</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                        R$ {dashboard.summary.total_inventory_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <DollarSign className="w-6 h-6 text-gray-500" />
                  </div>
                </div>
              </div>

              {/* Pneus por Marca */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Instalados por Marca
                </h3>
                <div className="space-y-3">
                  {dashboard.by_brand.map((brand) => (
                    <div key={brand.brand} className="flex items-center justify-between py-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{brand.brand}</p>
                        <div className="flex gap-4 mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {brand.count} pneus
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Prof. média: {brand.avg_tread ? parseFloat(brand.avg_tread).toFixed(1) : 0}mm
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gráficos e Análises */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Gráfico de Distribuição por Status */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Distribuição por Status
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={dashboard.by_status.map(item => ({
                          name: STATUS_LABELS[item.status] || item.status,
                          value: parseInt(item.count),
                          fill: CHART_COLORS[item.status] || '#9ca3af'
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {dashboard.by_status.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.status] || '#9ca3af'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Análise de Custos */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Análise de Custos
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Custo Médio por Pneu</span>
                        <DollarSign className="w-4 h-4 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        R$ {((parseFloat(dashboard.summary.total_inventory_cost) || 0) / ((parseInt(dashboard.summary.installed) || 0) + (parseInt(dashboard.summary.in_stock) || 0) || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Pneus Instalados</span>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        R$ {(((parseFloat(dashboard.summary.total_inventory_cost) || 0) * (parseInt(dashboard.summary.installed) || 0)) / ((parseInt(dashboard.summary.installed) || 0) + (parseInt(dashboard.summary.in_stock) || 0) || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {dashboard.summary.installed} pneus em uso
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Pneus em Estoque</span>
                        <Package className="w-4 h-4 text-blue-600" />
                      </div>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        R$ {(((parseFloat(dashboard.summary.total_inventory_cost) || 0) * (parseInt(dashboard.summary.in_stock) || 0)) / ((parseInt(dashboard.summary.installed) || 0) + (parseInt(dashboard.summary.in_stock) || 0) || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {dashboard.summary.in_stock} pneus disponíveis
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de Barras por Marca */}
              {dashboard.by_brand && dashboard.by_brand.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Pneus por Marca
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboard.by_brand}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="brand"
                        stroke="#9ca3af"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis
                        stroke="#9ca3af"
                        style={{ fontSize: '12px' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1f2937',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                        {dashboard.by_brand.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 40}, 70%, 50%)`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Métricas de Performance */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Métricas de Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Vida Útil Média</span>
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {dashboard.summary.avg_lifespan ?
                        `${parseInt(dashboard.summary.avg_lifespan).toLocaleString('pt-BR')} km` :
                        '40.000 km'}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <TrendingUp className="w-3 h-3 text-green-600" />
                      <span className="text-xs text-green-600">+8% vs mês anterior</span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Taxa de Uso</span>
                      <Activity className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {dashboard.summary.total_tires ? ((parseInt(dashboard.summary.installed) || 0) / (parseInt(dashboard.summary.total_tires) || 1) * 100).toFixed(1) : '75'}%
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <TrendingUp className="w-3 h-3 text-green-600" />
                      <span className="text-xs text-green-600">+3% vs mês anterior</span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Economia Estimada</span>
                      <DollarSign className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      R$ {dashboard.summary.estimated_savings ?
                        parseFloat(dashboard.summary.estimated_savings).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) :
                        '15.420,00'}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <TrendingDown className="w-3 h-3 text-red-600" />
                      <span className="text-xs text-red-600">-5% vs mês anterior</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top 5 Alertas Críticos */}
              {alerts && alerts.length > 0 && (
                <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        Alertas Críticos (Top 5)
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Pneus que requerem atenção imediata
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab('alerts')}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 flex items-center gap-1"
                    >
                      Ver Todos
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {alerts.slice(0, 5).map((alert, idx) => (
                      <div key={alert.id || idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          alert.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/30' :
                          alert.severity === 'high' ? 'bg-orange-100 dark:bg-orange-900/30' :
                          'bg-yellow-100 dark:bg-yellow-900/30'
                        }`}>
                          <AlertTriangle className={`w-4 h-4 ${
                            alert.severity === 'critical' ? 'text-red-600' :
                            alert.severity === 'high' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {alert.alert_type === 'low_pressure' ? 'Pressão Baixa' :
                                 alert.alert_type === 'low_tread' ? 'Profundidade Crítica' :
                                 alert.alert_type === 'rotation_due' ? 'Rodízio Necessário' :
                                 alert.alert_type}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Pneu #{alert.tire_id}
                                {alert.message && ` • ${alert.message}`}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                              {alert.severity === 'critical' ? 'Crítico' :
                               alert.severity === 'high' ? 'Alto' :
                               alert.severity === 'medium' ? 'Médio' : 'Baixo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Última Inspeção Unificada */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Última Inspeção de Pneus
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Do sistema unificado de inspeções
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/inspections')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver Todas
                  </button>
                </div>

                {lastInspection ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {lastInspection.vehicle_license_plate || 'Veículo não identificado'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(lastInspection.scheduled_date || lastInspection.created_at).toLocaleDateString('pt-BR')}
                          {lastInspection.inspector_name && ` • ${lastInspection.inspector_name}`}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        lastInspection.result === 'approved' ? 'bg-green-100 text-green-800' :
                        lastInspection.result === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {lastInspection.result === 'approved' ? 'Aprovado' :
                         lastInspection.result === 'failed' ? 'Reprovado' :
                         'Requer Atenção'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Inclui verificação de pressão e profundidade de sulco dos pneus
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma inspeção registrada
                    </p>
                    <button
                      onClick={() => navigate('/inspections')}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 mx-auto"
                    >
                      Criar primeira inspeção
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'by-vehicle' && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-300">Total Veículos</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-white">
                        {vehiclesWithTires.length}
                      </p>
                    </div>
                    <Truck className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-300">Pneus OK</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-white">
                        {vehiclesWithTires.reduce((sum, v) => sum + (v.stats?.ok || 0), 0)}
                      </p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900 dark:to-yellow-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 dark:text-yellow-300">Atenção</p>
                      <p className="text-2xl font-bold text-yellow-900 dark:text-white">
                        {vehiclesWithTires.reduce((sum, v) => sum + (v.stats?.warning || 0), 0)}
                      </p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900 dark:to-red-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 dark:text-red-300">Crítico</p>
                      <p className="text-2xl font-bold text-red-900 dark:text-white">
                        {vehiclesWithTires.reduce((sum, v) => sum + (v.stats?.critical || 0), 0)}
                      </p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
              </div>

              {/* Vehicle Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {vehiclesWithTires.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
                  >
                    {/* Header */}
                    <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {vehicle.license_plate}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {vehicle.brand} {vehicle.model}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          vehicle.stats.critical > 0
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : vehicle.stats.warning > 0
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {vehicle.stats.critical > 0
                            ? 'Crítico'
                            : vehicle.stats.warning > 0
                            ? 'Atenção'
                            : 'OK'}
                        </span>
                      </div>

                      {/* Quick Stats */}
                      <div className="mt-3 flex gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{vehicle.stats.ok}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{vehicle.stats.warning}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{vehicle.stats.critical}</span>
                        </div>
                        <div className="ml-auto text-gray-500 dark:text-gray-400">
                          {vehicle.stats.total} pneus
                        </div>
                      </div>
                    </div>

                    {/* Tire Map */}
                    <VehicleTireMap
                      vehicle={vehicle}
                      onTireClick={(tire, vehicleData) => {
                        setSelectedTireDetails(tire);
                        setShowDetailsModal(true);
                        loadTireHistory(tire.id);
                      }}
                      onEmptyPositionClick={(position, label) => {
                        setInstallPosition(position);
                        setInstallPositionLabel(label);
                        setInstallVehicle(vehicle);
                        setShowTireSelectionModal(true);
                      }}
                    />

                    {/* Alerts */}
                    {vehicle.alerts && vehicle.alerts.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">
                            {vehicle.alerts.length} alerta{vehicle.alerts.length > 1 ? 's' : ''} ativo{vehicle.alerts.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {vehiclesWithTires.length === 0 && (
                <div className="text-center py-12">
                  <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum veículo com pneus instalados
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tires' && (
            <div className="space-y-4">
              {/* Filtros Avançados */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Busca */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Buscar
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Nº série, marca ou modelo..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPage(1);
                        }}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      {searchTerm && (
                        <button
                          onClick={handleSearchClear}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="all">Todos</option>
                      <option value="installed">Instalados</option>
                      <option value="in_stock">Em Estoque</option>
                      <option value="storage">Armazenados</option>
                      <option value="damaged">Danificados</option>
                    </select>
                  </div>

                  {/* Itens por página */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Por página
                    </label>
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(parseInt(e.target.value));
                        setPage(1);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Tabela */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <TiresTable
                  tires={tires}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  onViewDetails={handleViewDetails}
                  onInstall={openInstallModal}
                />
              </div>

              {/* Paginação */}
              {pagination && pagination.totalPages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                />
              )}
            </div>
          )}

          {activeTab === 'installations' && (
            <div className="space-y-4">
              {/* Filtros */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data Início
                    </label>
                    <input
                      type="date"
                      value={installFilters.startDate}
                      onChange={(e) => setInstallFilters({...installFilters, startDate: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data Fim
                    </label>
                    <input
                      type="date"
                      value={installFilters.endDate}
                      onChange={(e) => setInstallFilters({...installFilters, endDate: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Placa
                    </label>
                    <input
                      type="text"
                      placeholder="ABC-1234"
                      value={installFilters.licensePlate}
                      onChange={(e) => setInstallFilters({...installFilters, licensePlate: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Veículo
                    </label>
                    <select
                      value={installFilters.vehicleId}
                      onChange={(e) => setInstallFilters({...installFilters, vehicleId: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Todos</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.model || vehicle.license_plate}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pneu
                    </label>
                    <select
                      value={installFilters.tireId}
                      onChange={(e) => setInstallFilters({...installFilters, tireId: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Todos</option>
                      {tires.map(tire => (
                        <option key={tire.id} value={tire.id}>
                          {tire.brand} {tire.model} - {tire.serial_number}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setInstallFilters({
                      startDate: '',
                      endDate: '',
                      licensePlate: '',
                      vehicleId: '',
                      tireId: ''
                    })}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Limpar Filtros
                  </button>
                </div>
              </div>

              {/* Tabela */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Pneu</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Placa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Veículo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Posição</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Instalação</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Remoção</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">KM Rodados</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredInstallations.map((inst) => (
                    <tr key={inst.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {inst.brand} {inst.model}
                        <br />
                        <span className="text-xs text-gray-500">{inst.serial_number}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {inst.license_plate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {inst.vehicle_brand && inst.vehicle_model ? (
                          <>
                            <div className="font-medium">{inst.vehicle_brand}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{inst.vehicle_model}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {inst.position}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(inst.installation_date).toLocaleDateString('pt-BR')}
                        <br />
                        <span className="text-xs">{inst.installation_odometer.toLocaleString('pt-BR')} km</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {inst.removal_date ? new Date(inst.removal_date).toLocaleDateString('pt-BR') : '-'}
                        {inst.removal_odometer && (
                          <>
                            <br />
                            <span className="text-xs">{inst.removal_odometer.toLocaleString('pt-BR')} km</span>
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {inst.km_used ? `${inst.km_used.toLocaleString('pt-BR')} km` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {installations.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhuma instalação registrada</p>
                </div>
              )}
              </div>
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data Início
                    </label>
                    <input
                      type="date"
                      value={alertFilters.startDate}
                      onChange={(e) => setAlertFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data Fim
                    </label>
                    <input
                      type="date"
                      value={alertFilters.endDate}
                      onChange={(e) => setAlertFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Placa do Veículo
                    </label>
                    <input
                      type="text"
                      value={alertFilters.licensePlate}
                      onChange={(e) => setAlertFilters(prev => ({ ...prev, licensePlate: e.target.value }))}
                      placeholder="Filtrar por placa..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Veículo
                    </label>
                    <input
                      type="text"
                      value={alertFilters.vehicle}
                      onChange={(e) => setAlertFilters(prev => ({ ...prev, vehicle: e.target.value }))}
                      placeholder="Filtrar por veículo..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pneu
                    </label>
                    <input
                      type="text"
                      value={alertFilters.tire}
                      onChange={(e) => setAlertFilters(prev => ({ ...prev, tire: e.target.value }))}
                      placeholder="Filtrar por número de série..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Alerts Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Severidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Pneu
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Placa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Veículo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Mensagem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredAlerts.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            Nenhum alerta encontrado
                          </td>
                        </tr>
                      ) : (
                        filteredAlerts.map((alert) => (
                          <tr key={alert.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                alert.severity === 'critical'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : alert.severity === 'high'
                                  ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                  : alert.severity === 'medium'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              }`}>
                                {alert.severity === 'critical' ? 'Crítica'
                                  : alert.severity === 'high' ? 'Alta'
                                  : alert.severity === 'medium' ? 'Média'
                                  : 'Baixa'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {alert.alert_type === 'low_pressure' ? 'Pressão Baixa'
                                : alert.alert_type === 'low_tread' ? 'Profundidade Baixa'
                                : alert.alert_type === 'uneven_wear' ? 'Desgaste Irregular'
                                : alert.alert_type === 'rotation_due' ? 'Rodízio Pendente'
                                : alert.alert_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {alert.tire?.serial_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {alert.vehicle?.license_plate || 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {alert.vehicle?.brand && alert.vehicle?.model ? (
                                <>
                                  <div className="font-medium">{alert.vehicle.brand}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{alert.vehicle.model}</div>
                                </>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {alert.message}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(alert.detected_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                alert.status === 'resolved'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {alert.status === 'resolved' ? 'Resolvido' : 'Ativo'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cadastro - Registration Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Cadastrar Pneu
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número de Série *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marca *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Modelo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tamanho *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: 295/80R22.5"
                      value={formData.size}
                      onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo de Pneu
                    </label>
                    <select
                      value={formData.tire_type}
                      onChange={(e) => setFormData({ ...formData, tire_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="new">Novo</option>
                      <option value="retreaded">Recapado</option>
                      <option value="used">Usado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Categoria
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="steer">Dianteiro</option>
                      <option value="drive">Tração</option>
                      <option value="trailer">Reboque</option>
                      <option value="spare">Estepe</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data de Compra
                    </label>
                    <input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Preço de Compra
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                      Vida Útil Esperada (km)
                      <span className="text-xs text-gray-500 dark:text-gray-400" title="Calculado automaticamente baseado em: Profundidade (mm) × 600 km/mm × Fator Tipo × Fator Categoria">
                        (calculado)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.expected_lifespan_km ? `${parseInt(formData.expected_lifespan_km).toLocaleString('pt-BR')} km` : '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                      title="Calculado automaticamente baseado em: Profundidade (mm) × 600 km/mm × Fator Tipo (Novo: 1.0, Recapado: 0.7, Usado: 0.4) × Fator Categoria (Dianteiro: 0.9, Tração: 1.0, Reboque: 1.1, Estepe: 1.5)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Profundidade Atual (mm)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.current_tread_depth_mm}
                      onChange={(e) => setFormData({ ...formData, current_tread_depth_mm: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                      Custo por mm
                      <span className="text-xs text-gray-500 dark:text-gray-400" title="Calculado automaticamente: Preço de Compra ÷ Profundidade Atual">
                        (calculado)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.cost_per_mm ? `R$ ${parseFloat(formData.cost_per_mm).toFixed(2)}` : '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                      title="Calculado automaticamente: Preço de Compra ÷ Profundidade Atual"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Cadastrar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Instalação */}
      {showInstallModal && selectedTire && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Instalar Pneu
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedTire.brand} {selectedTire.model} - {selectedTire.serial_number}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowInstallModal(false);
                    setSelectedTire(null);
                    resetInstallForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleInstallTire} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Veículo *
                  </label>
                  <select
                    required
                    value={installFormData.vehicle_id}
                    onChange={(e) => setInstallFormData({ ...installFormData, vehicle_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione um veículo</option>
                    {Array.isArray(vehicles) && vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.license_plate} - {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Posição *
                    </label>
                    <select
                      required
                      value={installFormData.position}
                      onChange={(e) => setInstallFormData({ ...installFormData, position: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Selecione a posição</option>
                      <option value="Dianteiro Esquerdo">Dianteiro Esquerdo</option>
                      <option value="Dianteiro Direito">Dianteiro Direito</option>
                      <option value="Traseiro Esquerdo Externo">Traseiro Esquerdo Externo</option>
                      <option value="Traseiro Esquerdo Interno">Traseiro Esquerdo Interno</option>
                      <option value="Traseiro Direito Externo">Traseiro Direito Externo</option>
                      <option value="Traseiro Direito Interno">Traseiro Direito Interno</option>
                      <option value="Estepe">Estepe</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data de Instalação *
                    </label>
                    <input
                      type="date"
                      required
                      value={installFormData.installation_date}
                      onChange={(e) => setInstallFormData({ ...installFormData, installation_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Hodômetro (km) *
                    </label>
                    <input
                      type="number"
                      required
                      placeholder="Ex: 125000"
                      value={installFormData.installation_odometer}
                      onChange={(e) => setInstallFormData({ ...installFormData, installation_odometer: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Profundidade do Sulco (mm)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Ex: 16.5"
                      value={installFormData.installation_tread_depth_mm}
                      onChange={(e) => setInstallFormData({ ...installFormData, installation_tread_depth_mm: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome do Instalador
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: João Silva"
                    value={installFormData.installer_name}
                    onChange={(e) => setInstallFormData({ ...installFormData, installer_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInstallModal(false);
                      setSelectedTire(null);
                      resetInstallForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Instalar Pneu
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Pneu */}
      {showDetailsModal && selectedTireDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Detalhes do Pneu - {selectedTireDetails.serial_number}
                </h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedTireDetails(null);
                    setTireHistory([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-4 mt-4">
                <button
                  onClick={() => setDetailsTab('info')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    detailsTab === 'info'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Informações
                </button>
                <button
                  onClick={() => setDetailsTab('history')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    detailsTab === 'history'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Histórico
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailsTab === 'info' ? (
                // Aba de Informações
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                        IDENTIFICAÇÃO
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Número de Série:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.serial_number}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Marca:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.brand}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Modelo:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.model}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Tamanho:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.size}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                        ESPECIFICAÇÕES
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Tipo:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.tire_type === 'new' ? 'Novo' :
                             selectedTireDetails.tire_type === 'retreaded' ? 'Recapado' : 'Usado'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Categoria:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.category === 'steer' ? 'Dianteiro' :
                             selectedTireDetails.category === 'drive' ? 'Tração' :
                             selectedTireDetails.category === 'trailer' ? 'Reboque' : 'Estepe'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Profundidade Atual:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.current_tread_depth_mm} mm
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
                          <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTireDetails.status)}`}>
                            {getStatusLabel(selectedTireDetails.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                        FINANCEIRO
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Data de Compra:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.purchase_date ? new Date(selectedTireDetails.purchase_date).toLocaleDateString('pt-BR') : '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Preço de Compra:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            R$ {parseFloat(selectedTireDetails.purchase_price || 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Custo por mm:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            R$ {parseFloat(selectedTireDetails.cost_per_mm || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
                        VIDA ÚTIL
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Vida Útil Esperada:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.expected_lifespan_km ? `${parseInt(selectedTireDetails.expected_lifespan_km).toLocaleString('pt-BR')} km` : '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">Km Rodados:</span>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedTireDetails.km_driven ? `${parseInt(selectedTireDetails.km_driven).toLocaleString('pt-BR')} km` : '0 km'}
                          </p>
                        </div>
                        {selectedTireDetails.expected_lifespan_km && selectedTireDetails.km_driven && (
                          <div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Percentual Usado:</span>
                            <p className="text-base font-medium text-gray-900 dark:text-white">
                              {Math.round((selectedTireDetails.km_driven / selectedTireDetails.expected_lifespan_km) * 100)}%
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedTireDetails.notes && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                        OBSERVAÇÕES
                      </h3>
                      <p className="text-base text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        {selectedTireDetails.notes}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                // Aba de Histórico
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Histórico Completo
                  </h3>

                  {tireHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhum histórico encontrado para este pneu</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {tireHistory.map((event, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <div className="flex items-start gap-3">
                            {/* Ícone por tipo de evento */}
                            <div className={`p-2 rounded-full ${
                              event.type === 'installation' ? 'bg-blue-100 dark:bg-blue-900' :
                              event.type === 'inspection' ? 'bg-green-100 dark:bg-green-900' :
                              'bg-red-100 dark:bg-red-900'
                            }`}>
                              {event.type === 'installation' ? (
                                <Wrench className={`w-5 h-5 ${
                                  event.type === 'installation' ? 'text-blue-600 dark:text-blue-400' : ''
                                }`} />
                              ) : event.type === 'inspection' ? (
                                <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                              )}
                            </div>

                            {/* Conteúdo do evento */}
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-900 dark:text-white">
                                  {event.type === 'installation' ? 'Instalação' :
                                   event.type === 'inspection' ? 'Inspeção' :
                                   'Alerta'}
                                </h4>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(event.date).toLocaleString('pt-BR')}
                                </span>
                              </div>

                              {/* Detalhes específicos por tipo */}
                              {event.type === 'installation' && (
                                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                  <p><span className="font-medium">Veículo:</span> {event.license_plate || '-'}</p>
                                  <p><span className="font-medium">Posição:</span> {event.position || '-'}</p>
                                  {event.installation_tread_depth_mm && (
                                    <p><span className="font-medium">Profundidade na instalação:</span> {event.installation_tread_depth_mm} mm</p>
                                  )}
                                  {event.installer_name && (
                                    <p><span className="font-medium">Instalador:</span> {event.installer_name}</p>
                                  )}
                                  {event.removal_date && (
                                    <p className="text-red-600 dark:text-red-400">
                                      <span className="font-medium">Removido em:</span> {new Date(event.removal_date).toLocaleDateString('pt-BR')}
                                    </p>
                                  )}
                                </div>
                              )}

                              {event.type === 'inspection' && (
                                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                  <p><span className="font-medium">Tipo:</span> {event.inspection_type || 'Rotina'}</p>
                                  {event.tread_depth_mm && (
                                    <p><span className="font-medium">Profundidade:</span> {event.tread_depth_mm} mm</p>
                                  )}
                                  {event.pressure_psi && (
                                    <p><span className="font-medium">Pressão:</span> {event.pressure_psi} PSI</p>
                                  )}
                                  {event.notes && (
                                    <p><span className="font-medium">Observações:</span> {event.notes}</p>
                                  )}
                                </div>
                              )}

                              {event.type === 'alert' && (
                                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                  <p><span className="font-medium">Tipo:</span> {event.alert_type || '-'}</p>
                                  <p>
                                    <span className="font-medium">Severidade:</span>{' '}
                                    <span className={`font-semibold ${
                                      event.severity === 'critical' ? 'text-red-600' :
                                      event.severity === 'high' ? 'text-orange-600' :
                                      event.severity === 'medium' ? 'text-yellow-600' :
                                      'text-blue-600'
                                    }`}>
                                      {event.severity === 'critical' ? 'Crítico' :
                                       event.severity === 'high' ? 'Alto' :
                                       event.severity === 'medium' ? 'Médio' : 'Baixo'}
                                    </span>
                                  </p>
                                  {event.message && (
                                    <p><span className="font-medium">Mensagem:</span> {event.message}</p>
                                  )}
                                  {event.status && (
                                    <p>
                                      <span className="font-medium">Status:</span>{' '}
                                      <span className={event.status === 'resolved' ? 'text-green-600' : 'text-yellow-600'}>
                                        {event.status === 'resolved' ? 'Resolvido' : 'Pendente'}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modais de Cadastro em Lote */}
      <BulkTireModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => {
          loadData();
          toast.success('Pneus cadastrados com sucesso!');
        }}
      />

      <CsvImportModal
        isOpen={showCsvModal}
        onClose={() => setShowCsvModal(false)}
        onSuccess={() => {
          loadData();
          toast.success('Pneus importados com sucesso!');
        }}
      />

      <TemplateManager
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={(template) => {
          console.log('Template selecionado:', template);
          toast.success(`Template "${template.name}" selecionado!`);
        }}
      />

      <TireSelectionModal
        isOpen={showTireSelectionModal}
        onClose={() => {
          setShowTireSelectionModal(false);
          setInstallPosition(null);
          setInstallPositionLabel(null);
          setInstallVehicle(null);
        }}
        vehicle={installVehicle}
        position={installPosition}
        positionLabel={installPositionLabel}
        onInstallSuccess={() => {
          loadData();
          loadVehicles();
          toast.success('Pneu instalado com sucesso!');
        }}
      />
    </div>
  );
};

export default TiresPage;